import React, { memo } from 'react';
import { Handle, Position, NodeProps, useReactFlow, NodeResizeControl } from '@xyflow/react';
import { Trash2, Copy } from 'lucide-react';
import clsx from 'clsx';
import { NODE_REGISTRY } from '../../config/nodeRegistry';
import { DATA_TYPE_COLORS } from '../../types';
import { useCanvasContext } from '../../context/CanvasContext';

// Import contents
import TextContent from './contents/TextContent';
import TextLabelContent from './contents/TextLabelContent';
import UploadImageContent from './contents/UploadImageContent';
import GenImageContent from './contents/GenImageContent';
import GenVideoContent from './contents/GenVideoContent';
import DynamicPromptContent from './contents/DynamicPromptContent';
import { ResultImageContent, ResultVideoContent } from './contents/ResultContent';

const COMPONENT_MAP: Record<string, React.FC<any>> = {
  'text-node': TextContent,
  'text-label': TextLabelContent,
  'dynamic-prompt': DynamicPromptContent,
  'upload-image': UploadImageContent,
  'gen-image': GenImageContent,
  'gen-video': GenVideoContent,
  'result-image': ResultImageContent,
  'result-video': ResultVideoContent,
  'group': () => null,
};

// Custom Resize Handle Icon (Corner Bracket)
const ResizeHandleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-slate-400 group-hover:text-blue-400 transition-colors opacity-100"
  >
    {/* Bottom-Right Corner Shape */}
    <path d="M22 10V22H10" />
  </svg>
);

const BaseNode = ({ id, data, type, selected }: NodeProps) => {
  const { deleteElements, setNodes, getNode, updateNodeData } = useReactFlow();
  const { openMenu } = useCanvasContext();
  const config = NODE_REGISTRY[type || ''];

  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');

  if (!config) return <div className="text-red-500 bg-black p-2 rounded">Unknown Node Type: {type}</div>;

  const isResizable = !!config.resizable;

  const handleDelete = () => deleteElements({ nodes: [{ id }] });

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue((data.customLabel as string) || `${config.label}:${id.slice(-3)}`);
  };

  const handleSave = () => {
    setIsEditing(false);
    const defaultLabel = `${config.label}:${id.slice(-3)}`;
    if (editValue.trim() !== defaultLabel) {
      updateNodeData(id, { customLabel: editValue.trim() });
    } else {
      updateNodeData(id, { customLabel: undefined });
    }
  };

  if (config.isGroup) {
    return (
      <div
        className={clsx(
          "absolute top-0 left-0 w-full h-full rounded-lg transition-all duration-200",
          "border-blue-300/30 border border-dashed",
          "bg-transparent",
          "p-3",
          "overflow-visible"  // 允许子节点溢出可见
        )}
      >
        {/* Group Title - Editable */}
        <div className="absolute top-2 left-3">
          {isEditing ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={handleSave}
              onKeyDown={(e) => e.key === 'Enter' && handleSave()}
              autoFocus
              className="text-[10px] font-semibold text-blue-200 bg-blue-900/50
                         border border-blue-400 rounded px-1 outline-none
                       max-w-[200px]"
              maxLength={30}
            />
          ) : (
            <span
              onClick={handleEdit}
              className="text-[10px] font-semibold text-blue-300/70
                        cursor-pointer hover:text-blue-200 select-none
                        max-w-[200px] inline-block truncate"
              title="点击编辑标题"
            >
              {(data.customLabel as string) || `Group:${id.slice(-3)}`}
            </span>
          )}
        </div>

        {/* Resize Control - Bottom Right Corner */}
        {config.resizable && (
          <NodeResizeControl
            minWidth={200}
            minHeight={100}
            style={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              border: 'none',
              background: 'transparent',
              zIndex: 40,
            }}
          >
            <div className="cursor-nwse-resize group flex items-center justify-center w-4 h-4 p-0">
              <ResizeHandleIcon />
            </div>
          </NodeResizeControl>
        )}
      </div>
    );
  }

  const handleCopy = () => {
    const node = getNode(id);
    if (!node) return;

    const { customLabel, ...dataWithoutLabel } = node.data;

    const newNode = {
      ...node,
      id: `${node.type}-${Date.now()}`,
      position: { x: node.position.x + 40, y: node.position.y + 40 },
      selected: true,
      data: dataWithoutLabel,
    };

    // Use setNodes to atomically deselect existing nodes and add the new one
    setNodes((nodes) => [
      ...nodes.map((n) => ({ ...n, selected: false })),
      newNode
    ]);
  };

  const ContentComponent = COMPONENT_MAP[type || ''] || (() => null);

  return (
    <div className={clsx(
      // Base Layout & Appearance
      "relative rounded-lg shadow-xl font-sans flex flex-col group",

      // 优化背景: 添加渐变和透明度
      "bg-gradient-to-br from-slate-800/95 to-slate-900/95 backdrop-blur-sm",

      // Animation Properties: Standard transition, no transforms on the container to avoid blur
      "transition-all duration-200 border",

      // State Styles (ComfyUI Style: High contrast borders)
      selected
        ? "border-blue-500 shadow-[0_0_0_2px_rgba(59,130,246,1)] z-50" // Solid blue ring when selected
        : "border-slate-700 hover:border-blue-400 hover:shadow-[0_0_15px_rgba(96,165,250,0.3)]", // Glow and blue border on hover

      // Size Constraints
      isResizable ? "w-full h-full min-w-[200px] min-h-[100px]" : "min-w-[240px] max-w-[300px]"
    )}>

      {/* Node Header */}
      <div className="flex justify-between items-center p-2 rounded-t-lg border-b border-slate-700/50 h-8 shrink-0
        bg-gradient-to-r from-slate-700/70 via-slate-700/50 to-slate-700/70">
        {/* Delete Button */}
        {config.canDelete ? (
          <button
            onClick={handleDelete}
            className="text-slate-600 hover:text-red-400 transition p-1 rounded-sm hover:bg-slate-800"
            title="Delete Node"
          >
            <Trash2 size={12} />
          </button>
        ) : <div className="w-5" />}

        {/* Title - 优化文字颜色 */}
        {isEditing ? (
          <input
            type="text"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleSave}
            onKeyDown={(e) => e.key === 'Enter' && handleSave()}
            autoFocus
            className="text-[11px] font-bold text-slate-100 bg-slate-700 border border-blue-400 rounded px-1 w-full outline-none"
          />
        ) : (
          <span
            onClick={handleEdit}
            className="text-[11px] font-bold text-slate-100 select-none cursor-pointer hover:text-blue-300 transition-colors"
          >
            {(data.customLabel as string) || `${config.label}:${id.slice(-3)}`}
          </span>
        )}

        {/* Copy Button */}
        {config.canCopy ? (
          <button
            onClick={handleCopy}
            className="text-slate-600 hover:text-blue-400 transition p-1 rounded-sm hover:bg-slate-800"
            title="Duplicate Node"
          >
            <Copy size={12} />
          </button>
        ) : <div className="w-5" />}
      </div>

      {/* Inputs (Left Side) */}
      <div className="absolute -left-[14px] top-10 flex flex-col gap-5 py-2 z-10">
        {config.inputs.map((input) => {
          // For gen-video node, determine if input should be visible based on current mode
          let isVisible = true;
          if (type === 'gen-video' && data.currentMode) {
            const currentMode = data.currentMode;
            const visibleInputs: string[] = ['in_text', 'in_text_negative'];

            // Add mode-specific inputs in fixed order
            // 🔴 更新：移除reference和extend模式，interpolate模式移除in_img_ref
            if (currentMode === 'image') {
              visibleInputs.push('in_img_ref');
            }
            if (currentMode === 'interpolate') {
              // 🔴 移除 in_img_ref，interpolate模式不再支持reference_images
              visibleInputs.push('in_img_first');
              visibleInputs.push('in_img_last');
            }

            isVisible = visibleInputs.includes(input.key);
          }

          return (
            <div
              key={input.key}
              className="relative flex items-center h-4 group/handle cursor-pointer"
              onClick={(e) => {
                // Trigger context menu for input
                e.stopPropagation();
                openMenu(e.clientX, e.clientY, id, input.key, input.type, true); // isInput = true
              }}
              style={{ opacity: isVisible ? 1 : 0, pointerEvents: isVisible ? 'auto' : 'none' }}
            >
              <Handle
                type="target"
                position={Position.Left}
                id={input.key}
                className={clsx(
                  // Override RF positioning with flow layout to prevent transform conflicts
                  "!w-3.5 !h-3.5 !border-2 !border-slate-800 transition-transform !relative !top-auto !left-auto !transform-none hover:!scale-125",
                  DATA_TYPE_COLORS[input.type]
                )}
              />
              <span className="absolute left-4 text-[9px] font-mono text-slate-400 opacity-0 group-hover/handle:opacity-100 transition-opacity bg-black/80 px-1.5 py-0.5 rounded whitespace-nowrap pointer-events-none z-50">
                {input.label} ({input.type})
              </span>
            </div>
          );
        })}
      </div>

      {/* Outputs (Right Side) */}
      <div className="absolute -right-[14px] top-10 flex flex-col gap-5 py-2 z-10">
        {config.outputs.map((output) => (
          <div
            key={output.key}
            className="relative flex items-center justify-end h-4 group/handle cursor-pointer"
            onClick={(e) => {
              // Trigger context menu for output
              e.stopPropagation();
              openMenu(e.clientX, e.clientY, id, output.key, output.type, false); // isInput = false
            }}
          >
            <Handle
              type="source"
              position={Position.Right}
              id={output.key}
              className={clsx(
                // Override RF positioning with flow layout to prevent transform conflicts
                "!w-3.5 !h-3.5 !border-2 !border-slate-800 transition-transform !relative !top-auto !left-auto !transform-none hover:!scale-125",
                DATA_TYPE_COLORS[output.type]
              )}
            />
            <span className="absolute right-4 text-[9px] font-mono text-slate-400 opacity-0 group-hover/handle:opacity-100 transition-opacity bg-black/80 px-1.5 py-0.5 rounded whitespace-nowrap z-50 pointer-events-none">
              {output.label} ({output.type})
            </span>
          </div>
        ))}
      </div>

      {/* Content Body - Grow to fill remaining height if resizable */}
      <div className={clsx(
        "p-3 text-slate-200 relative bg-gradient-to-b from-transparent to-slate-900/20",
        isResizable ? "flex-1 min-h-0 overflow-hidden" : ""
      )}>
        <ContentComponent data={data} id={id} />
      </div>

      {/* Custom Resizer Control (Bottom-Right Corner) */}
      {isResizable && (
        <NodeResizeControl
          minWidth={200}
          minHeight={100}
          // Use inline style for absolute positioning to ensure it stays in the corner of the container
          style={{
            position: 'absolute',
            bottom: 0,
            right: 0,
            border: 'none',
            background: 'transparent',
            zIndex: 40,
          }}
        >
          {/* Corner Symbol Icon */}
          <div className="cursor-nwse-resize group flex items-center justify-center w-4 h-4 p-0">
            <ResizeHandleIcon />
          </div>
        </NodeResizeControl>
      )}

    </div>
  );
};

export default memo(BaseNode);